import { useEffect, useState } from "react";
import { CheckCircle2, Circle, Clock, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [opps, setOpps] = useState([]);
  const [filter, setFilter] = useState("open"); // open | all | done
  const [show, setShow] = useState(false);

  const load = () => {
    const params = {};
    if (filter === "open") params.completed = false;
    if (filter === "done") params.completed = true;
    api.get("/tasks", { params }).then((r) => setTasks(r.data));
  };

  useEffect(() => {
    api.get("/opportunities", { params: { limit: 200 } }).then((r) => setOpps(r.data));
  }, []);
  useEffect(load, [filter]);

  const toggle = async (aid, completed) => {
    await api.patch(`/tasks/${aid}`, { completed: !completed });
    load();
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Productivity</div>
          <h1 className="font-heading text-4xl tracking-tight">Tasks & Follow-ups</h1>
          <p className="text-sm text-slate-400 mt-1">Email reminders sent 15 minutes before due time</p>
        </div>
        <button onClick={() => setShow(true)} data-testid="new-task-btn" className="btn-primary text-sm inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> New Task
        </button>
      </header>

      <div className="flex gap-2">
        {["open", "all", "done"].map((f) => (
          <button
            key={f}
            data-testid={`task-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-all ${filter === f ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/50" : "border-white/10 text-slate-400 hover:bg-white/5"}`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="ae-card divide-y divide-white/5">
        {tasks.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Clock className="h-8 w-8 mx-auto text-slate-700 mb-2" />
            No tasks yet. Create one to track follow-ups.
          </div>
        ) : tasks.map((t) => {
          const due = new Date(t.due_at);
          const overdue = !t.completed && due < new Date();
          return (
            <div key={t.aid} data-testid={`task-row-${t.aid}`} className="p-4 flex items-center gap-3 hover:bg-white/[0.03]">
              <button onClick={() => toggle(t.aid, t.completed)} data-testid={`task-toggle-${t.aid}`} className="shrink-0">
                {t.completed ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <Circle className="h-5 w-5 text-slate-500 hover:text-emerald-400" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${t.completed ? "line-through text-slate-500" : "text-slate-100"}`}>{t.title}</div>
                {t.body && <div className="text-xs text-slate-500 mt-0.5 truncate">{t.body}</div>}
              </div>
              <div className={`text-xs font-mono tabular-nums ${overdue ? "text-rose-400" : "text-slate-400"}`}>
                {due.toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>

      {show && <NewTaskDialog opps={opps} onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
    </div>
  );
}

function NewTaskDialog({ opps, onClose, onCreated }) {
  const defaultDue = new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16); // local datetime input
  const [f, setF] = useState({ title: "", body: "", due_at: defaultDue, opportunity_oid: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tasks", {
        ...f,
        due_at: new Date(f.due_at).toISOString(),
        opportunity_oid: f.opportunity_oid || null,
      });
      toast.success("Task created");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
      <form onSubmit={submit} className="ae-card p-6 max-w-md w-full" data-testid="new-task-dialog">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400">New Task</div>
        <h2 className="font-heading text-2xl tracking-tight mt-1">Follow-up</h2>
        <div className="space-y-3 mt-4 text-sm">
          <input data-testid="task-title" required placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="inp" />
          <textarea data-testid="task-body" rows={2} placeholder="Notes (optional)" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="inp" />
          <select data-testid="task-opp" value={f.opportunity_oid} onChange={(e) => setF({ ...f, opportunity_oid: e.target.value })} className="inp">
            <option value="">(Link to opportunity, optional)</option>
            {opps.map((o) => <option key={o.oid} value={o.oid}>{o.title}</option>)}
          </select>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Due at</label>
            <input data-testid="task-due" type="datetime-local" required value={f.due_at} onChange={(e) => setF({ ...f, due_at: e.target.value })} className="inp font-mono" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} data-testid="task-submit" className="btn-primary text-sm">{busy ? "Creating…" : "Create"}</button>
        </div>
        <style>{`.inp { width: 100%; background: #0A0D12; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; padding: 0.5rem 0.625rem; font-size: 0.875rem; color: #F8FAFC; outline: none; }`}</style>
      </form>
    </div>
  );
}
